import React, { useState, useEffect } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Send, Message, PhoneEnabled } from '@mui/icons-material';
import twilioService, { TwilioMessage, TwilioPhoneNumber } from '../../services/twilioService';
import { useOrganization } from '../../pages/Organization/OrganizationContext';

const TwilioMessagesComponent: React.FC = () => {
  const { organization } = useOrganization();
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [messages, setMessages] = useState<TwilioMessage[]>([]);
  const [phoneNumbers, setPhoneNumbers] = useState<TwilioPhoneNumber[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(10);
  const [openSendDialog, setOpenSendDialog] = useState(false);
  const [messageBody, setMessageBody] = useState('');
  const [toNumber, setToNumber] = useState('');
  const [fromNumber, setFromNumber] = useState('');
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });

  useEffect(() => {
    if (organization?._id) {
      fetchMessages();
      fetchPhoneNumbers();
    }
  }, [organization, page, limit]);

  const fetchMessages = async () => {
    if (!organization?._id) return;

    setLoading(true);
    setError(null);

    try {
      const response = await twilioService.listMessages(page, limit);
      setMessages(response.data || []);
      setTotal(response.total || 0);
    } catch (err) {
      setError('Failed to fetch messages');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPhoneNumbers = async () => {
    if (!organization?._id) return;

    try {
      const numbers = await twilioService.listPhoneNumbers();
      setPhoneNumbers(numbers);

      if (numbers.length > 0 && !fromNumber) {
        setFromNumber(numbers[0].phoneNumber);
      }
    } catch (err) {
      console.error('Failed to fetch phone numbers:', err);
    }
  };

  const handleSendMessage = async () => {
    if (!organization?._id || !toNumber || !messageBody) return;

    setSending(true);
    setError(null);

    try {
      await twilioService.sendMessage(
        toNumber,
        messageBody,
        fromNumber
      );

      // Close dialog and reset form
      setOpenSendDialog(false);
      setMessageBody('');
      setToNumber('');

      // Refresh messages list
      fetchMessages();
    } catch (err) {
      setError('Failed to send message');
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  const handleOpenSendDialog = () => {
    setOpenSendDialog(true);
  };

  const handleCloseSendDialog = () => {
    setOpenSendDialog(false);
  };

  const handlePaginationModelChange = (newModel: { pageSize: number, page: number }) => {
    setPaginationModel(newModel);
    setPage(newModel.page + 1);
    setLimit(newModel.pageSize);
  };

  const formatPhoneNumber = (phoneNumber: string) => {
    if (!phoneNumber) return '';
    return phoneNumber.replace(/^\+1/, '').replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'sent':
        return 'bg-blue-100 text-blue-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'queued':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'dateCreated',
      headerName: 'Date',
      width: 170,
      valueFormatter: (params) => {
        const date = new Date(params.value);
        return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
      }
    },
    {
      field: 'direction',
      headerName: 'Direction',
      width: 100,
      renderCell: (params) => (
        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${params.value === 'inbound' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
          }`}>
          {params.value === 'inbound' ? 'Inbound' : 'Outbound'}
        </span>
      )
    },
    {
      field: 'from',
      headerName: 'From',
      width: 140,
      valueFormatter: (params) => formatPhoneNumber(params.value)
    },
    {
      field: 'to',
      headerName: 'To',
      width: 140,
      valueFormatter: (params) => formatPhoneNumber(params.value)
    },
    {
      field: 'body',
      headerName: 'Message',
      flex: 1,
      renderCell: (params) => (
        <div title={params.value} className="max-w-full">
          <p className="truncate text-sm">{params.value}</p>
        </div>
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(params.value)}`}>
          {params.value}
        </span>
      )
    },
  ];

  return (
    <div className="bg-white rounded-lg shadow-md">
      <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
        <h2 className="text-xl font-semibold">Twilio Messages</h2>
        <button
          onClick={handleOpenSendDialog}
          disabled={phoneNumbers.length === 0}
          className={`flex items-center px-4 py-2 rounded-md text-white font-medium ${phoneNumbers.length === 0
            ? 'bg-blue-300 cursor-not-allowed'
            : 'bg-blue-600 hover:bg-blue-700'
            }`}
        >
          <Send className="w-4 h-4 mr-2" />
          Send Message
        </button>
      </div>
      <hr className="border-gray-200" />
      <div className="p-6">
        {error && (
          <div className="mb-4 px-4 py-3 bg-red-100 text-red-700 rounded-md" role="alert">
            {error}
          </div>
        )}

        {phoneNumbers.length === 0 && (
          <div className="mb-4 px-4 py-3 bg-blue-100 text-blue-700 rounded-md" role="alert">
            You need to purchase at least one phone number before you can send messages.
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center p-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Message className="text-gray-300" style={{ fontSize: 48 }} />
            <h3 className="mt-2 text-lg font-medium">No Messages</h3>
            <p className="text-sm text-gray-500">
              No messages have been sent or received yet.
            </p>
          </div>
        ) : (
          <div className="h-[600px] w-full">
            <DataGrid
              rows={messages.map(msg => ({ ...msg, id: msg.sid }))}
              columns={columns}
              pagination
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationModelChange}
              pageSizeOptions={[10, 25, 50]}
              className="border-0 shadow-none"
              rowCount={total}
            />
          </div>
        )}
      </div>

      {/* Modal for sending a new message */}
      {openSendDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-xl font-semibold">Send SMS Message</h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    From Phone Number
                  </label>
                  <select
                    value={fromNumber}
                    onChange={(e) => setFromNumber(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {phoneNumbers.map((number) => (
                      <option key={number.sid} value={number.phoneNumber}>
                        {formatPhoneNumber(number.phoneNumber)} ({number.friendlyName})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    To Phone Number
                  </label>
                  <input
                    type="text"
                    value={toNumber}
                    onChange={(e) => setToNumber(e.target.value)}
                    placeholder="+15551234567"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="col-span-1 md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Message
                  </label>
                  <textarea
                    value={messageBody}
                    onChange={(e) => setMessageBody(e.target.value)}
                    rows={4}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  ></textarea>
                  <p className="mt-1 text-xs text-gray-500">
                    {messageBody.length}/1600 characters
                  </p>
                </div>
              </div>
            </div>
            <div className="px-6 py-3 bg-gray-50 flex justify-end gap-2 border-t border-gray-200">
              <button
                onClick={handleCloseSendDialog}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={handleSendMessage}
                disabled={sending || !toNumber || !messageBody || !fromNumber}
                className={`flex items-center px-4 py-2 rounded-md text-white font-medium ${sending || !toNumber || !messageBody || !fromNumber
                  ? 'bg-blue-300 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
                  }`}
              >
                {sending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <PhoneEnabled className="w-4 h-4 mr-2" />
                    Send Message
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TwilioMessagesComponent; 